import { DiagnosticEntry, DiagnosticEntryType, HelpRequestDiagnostics } from "../http/types/helpRequests";

const MAX_ENTRIES = 50;
const MAX_TEXT_LENGTH = 2000;

const entries: DiagnosticEntry[] = [];
let isInstalled = false;

const truncate = (text: string) => (text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text);

const stringify = (value: unknown): string => {
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // circular structures and the like
    return String(value);
  }
};

const addEntry = (type: DiagnosticEntryType, values: unknown[]) => {
  try {
    const error = values.find((x): x is Error => x instanceof Error);
    entries.push({
      type,
      message: truncate(values.map(stringify).join(" ")),
      stack: error?.stack ? truncate(error.stack) : undefined,
      timestamp: new Date().toISOString(),
    });
    if (entries.length > MAX_ENTRIES) {
      entries.shift();
    }
  } catch {
    // diagnostics must never break the app
  }
};

/**
 * Keeps the latest console errors/warnings and uncaught errors in memory, to attach them to a help
 * request. Call it once, as early as possible, before the app renders.
 */
export const installDiagnosticsCapture = () => {
  if (isInstalled) {
    return;
  }
  isInstalled = true;

  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...values: unknown[]) => {
    addEntry("console.error", values);
    originalError.apply(console, values);
  };
  console.warn = (...values: unknown[]) => {
    addEntry("console.warn", values);
    originalWarn.apply(console, values);
  };

  window.addEventListener("error", (event) => {
    addEntry("error", [event.error ?? event.message, event.filename ? `(${event.filename}:${event.lineno}:${event.colno})` : ""]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    addEntry("unhandledrejection", [event.reason]);
  });
};

export const buildDiagnostics = (appScope: string, language: string): HelpRequestDiagnostics => ({
  pageUrl: window.location.href,
  appScope,
  language,
  userAgent: navigator.userAgent,
  viewport: { width: window.innerWidth, height: window.innerHeight },
  screen: { width: window.screen.width, height: window.screen.height, devicePixelRatio: window.devicePixelRatio },
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  capturedAt: new Date().toISOString(),
  entries: [...entries],
});
